import { getDefaultStaffImage } from "@/utils/avatar.utils";
import { RoleEnum } from "@/types/user.definition";
import {
  Edit,
  Mail,
  Phone,
  RefreshCw,
  formatPhoneNumberForDisplay,
} from "@ews-admin/global-design-system";
import React from "react";
import { useTranslation } from "react-i18next";
import { Staff } from "../../../types/staff.definition";
import { CivilityEnum } from "../../../types/user.definition";

export interface SecretaryCardProps {
  member: Staff;
  onEditStaff: (staff: Staff) => void;
  onViewDetails: (staff: Staff) => void;
}

export const SecretaryCard: React.FC<SecretaryCardProps> = ({
  member,
  onEditStaff,
  onViewDetails,
}) => {
  const { t } = useTranslation();

  // Get contact information
  const email = member.contactInfo?.email || member.email || "";
  const phoneNumber = formatPhoneNumberForDisplay(
    member.contactInfo?.phoneNumber || member.phoneNumber || ""
  );

  return (
    <div
      className="overflow-hidden relative bg-white rounded-xl border transition-all duration-300 cursor-pointer border-neutral-200 hover:shadow-xl hover:shadow-secondary-50/50 hover:border-ews-secondary hover:-translate-y-1 group"
      onClick={() => onViewDetails(member)}
    >
      {/* Purple Header Background for Secretary */}
      <div className="absolute top-0 right-0 left-0 h-20 bg-ews-secondary"></div>

      {/* Action Buttons - Show on Hover */}
      <div className="flex absolute top-3 right-3 z-10 space-x-2 opacity-0 transition-opacity duration-200 group-hover:opacity-100">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onEditStaff(member);
          }}
          className="p-2 bg-white rounded-lg shadow-md transition-all hover:bg-secondary-50 hover:scale-110"
          title={t("staff.manage.list.edit")}
        >
          <Edit className="w-4 h-4 text-ews-secondary" />
        </button>
      </div>

      {/* Profile Section */}
      <div className="relative px-4 pt-6 pb-4">
        {/* Password Change Required Badge */}
        {member.requiresPasswordChange && (
          <div className="absolute top-2 left-2 z-10">
            <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-yellow-800 bg-yellow-100 border border-yellow-300 rounded-full">
              <RefreshCw className="mr-1 w-3 h-3" />
              {t("staff.manage.list.passwordChangeRequired", "Password Required")}
            </span>
          </div>
        )}
        {/* Profile Photo */}
        <div className="relative mx-auto mb-3 w-16 h-16">
          <div className="absolute inset-0 rounded-full bg-ews-secondary p-0.5">
            <img
              src={
                member.photoUrl ||
                getDefaultStaffImage(RoleEnum.SECRETARY, member.civility)
              }
              alt={`${member.firstName} ${member.lastName}`}
              className="object-cover w-full h-full rounded-full border-4 border-white"
            />
          </div>
        </div>

        {/* Name and Title */}
        <div className="mb-3 text-center">
          <h3 className="mb-1 text-lg font-bold text-neutral-900">
            {member.firstName} {member.lastName}
          </h3>

          {/* Role Badge */}
          <div className="flex justify-center mb-2">
            <span className="px-2.5 py-1 text-xs font-semibold rounded-full bg-ews-secondary text-white">
              {t("staff.manage.list.secretary")}
            </span>
          </div>
        </div>

        {/* Divider */}
        <div className="mb-4 h-px bg-neutral-200"></div>

        {/* Contact Information */}
        <div className="space-y-2.5">
          {email && (
            <div className="flex items-center text-sm text-neutral-600 group/item">
              <div className="flex justify-center items-center mr-2 w-8 h-8 rounded-lg transition-colors bg-neutral-100 group-hover/item:bg-secondary-50">
                <Mail className="w-3.5 h-3.5 text-neutral-500 group-hover/item:text-ews-secondary" />
              </div>
              <span className="text-xs truncate">{email}</span>
            </div>
          )}
          {phoneNumber && (
            <div className="flex items-center text-sm text-neutral-600 group/item">
              <div className="flex justify-center items-center mr-2 w-8 h-8 rounded-lg transition-colors bg-neutral-100 group-hover/item:bg-secondary-50">
                <Phone className="w-3.5 h-3.5 text-neutral-500 group-hover/item:text-ews-secondary" />
              </div>
              <span className="text-xs">{phoneNumber}</span>
            </div>
          )}
        </div>
      </div>

      {/* Footer with role info */}
      <div className="px-4 py-3 border-t bg-secondary-50 border-neutral-100">
        <div className="flex justify-between items-center text-xs text-neutral-600">
          <span className="font-medium text-ews-secondary truncate">
            {t("staff.manage.list.secretary")}
          </span>
          {member.civility && (
            <span className="text-neutral-500">
              {/* MED-1653: civility is stored as MAN / WOMAN, not MR / MRS */}
              {member.civility === CivilityEnum.MR
                ? t("common.civility.mr")
                : t("common.civility.mrs")}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};
